import React, { useEffect, useRef, useState } from 'react';
import './MessageActions.css';

function Icon({ name }) {
  const common = { width: 20, height: 20, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true };
  if (name === 'copy') return <svg {...common}><rect x="9" y="9" width="10" height="10" rx="2" /><rect x="5" y="5" width="10" height="10" rx="2" /></svg>;
  if (name === 'like') return <svg {...common}><path d="M7 10v10H4V10h3Z" /><path d="M9 20h7.6a2 2 0 0 0 1.9-1.4l1.3-4.2A2 2 0 0 0 17.9 12H14l.6-3.1A3.2 3.2 0 0 0 11.5 5L9 10v10Z" /></svg>;
  if (name === 'speaker') return <svg {...common}><path d="M5 9v6h4l5 4V5L9 9H5Z" /><path d="M17 9a4 4 0 0 1 0 6" /><path d="M19.5 6.5a8 8 0 0 1 0 11" /></svg>;
  if (name === 'share') return <svg {...common}><circle cx="18" cy="5" r="2.5" /><circle cx="6" cy="12" r="2.5" /><circle cx="18" cy="19" r="2.5" /><path d="m8.2 10.8 7.5-4.4" /><path d="m8.2 13.2 7.5 4.4" /></svg>;
  return <svg {...common}><circle cx="12" cy="5" r="1" fill="currentColor" stroke="none" /><circle cx="12" cy="12" r="1" fill="currentColor" stroke="none" /><circle cx="12" cy="19" r="1" fill="currentColor" stroke="none" /></svg>;
}

export default function MessageActions({ text }) {
  const safeText = String(text || '').trim();
  const [copied, setCopied] = useState(false);
  const [liked, setLiked] = useState(false);
  const [disliked, setDisliked] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const copiedTimerRef = useRef(null);

  useEffect(() => () => {
    if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) window.speechSynthesis.cancel();
  }, []);

  const copyText = async () => {
    if (!safeText) return;
    try {
      await navigator.clipboard.writeText(safeText);
      setCopied(true);
      if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
      copiedTimerRef.current = setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopied(false);
    }
  };

  const toggleLike = () => {
    setLiked(current => !current);
    setDisliked(false);
  };

  const toggleSpeech = () => {
    if (!safeText || typeof window === 'undefined' || !('speechSynthesis' in window)) return;
    const synth = window.speechSynthesis;
    if (speaking) {
      synth.cancel();
      setSpeaking(false);
      return;
    }
    synth.cancel();
    const utterance = new SpeechSynthesisUtterance(safeText);
    utterance.lang = /[\u0590-\u05FF]/.test(safeText) ? 'he-IL' : 'en-US';
    utterance.onend = () => setSpeaking(false);
    utterance.onerror = () => setSpeaking(false);
    setSpeaking(true);
    synth.speak(utterance);
  };

  const shareText = async () => {
    if (!safeText) return;
    try {
      if (navigator.share) {
        await navigator.share({ text: safeText });
        return;
      }
      await navigator.clipboard.writeText(safeText);
      setCopied(true);
      if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
      copiedTimerRef.current = setTimeout(() => setCopied(false), 1600);
    } catch {
      // The native share sheet may be cancelled by the customer; no chat state should change.
    }
  };

  if (!safeText) return null;

  return (
    <div className="message-actions" aria-label="פעולות על תשובת הסוכן">
      <button type="button" className={`message-action ${copied ? 'is-active' : ''}`} onClick={copyText} aria-label={copied ? 'הועתק' : 'העתק תשובה'} title={copied ? 'הועתק' : 'העתק'}>
        <Icon name="copy" />
      </button>
      <button type="button" className={`message-action ${liked ? 'is-active' : ''}`} onClick={toggleLike} aria-pressed={liked} aria-label="אהבתי את התשובה" title="אהבתי">
        <Icon name="like" />
      </button>
      <button type="button" className={`message-action ${speaking ? 'is-active' : ''}`} onClick={toggleSpeech} aria-pressed={speaking} aria-label={speaking ? 'הפסק הקראה' : 'הקרא תשובה'} title={speaking ? 'הפסק הקראה' : 'הקראה'}>
        <Icon name="speaker" />
      </button>
      <button type="button" className="message-action" onClick={shareText} aria-label="שתף תשובה" title="שיתוף">
        <Icon name="share" />
      </button>
      <div className="message-actions-more">
        <button type="button" className={`message-action ${menuOpen ? 'is-active' : ''}`} onClick={() => setMenuOpen(current => !current)} aria-expanded={menuOpen} aria-haspopup="menu" aria-label="אפשרויות נוספות" title="אפשרויות נוספות">
          <Icon name="more" />
        </button>
        {menuOpen && (
          <div className="message-actions-menu" role="menu">
            <button type="button" role="menuitem" className={disliked ? 'is-selected' : ''} onClick={() => { setDisliked(current => !current); setLiked(false); setMenuOpen(false); }}>
              {disliked ? 'סומן כלא מועיל' : 'התשובה לא מועילה'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
